import { createRouter, createWebHistory } from "vue-router";
import { supabase } from "@/supabase";

// 🌐 Core Pages
import HomePage from "@/views/HomePage.vue";
import WhitepaperPage from "@/views/WhitepaperPage.vue";
import AboutPage from "@/views/AboutPage.vue";
import FeedbackView from "@/views/FeedbackView.vue";
import LoginPage from "@/views/LoginPage.vue";
import RegisterPage from "@/views/RegisterPage.vue";
import AuthCallback from "@/views/AuthCallback.vue";
import SubmitStoryView from "@/views/SubmitStoryView.vue";
import ContestDetail from "@/pages/ContestDetail.vue";

// 🤖 Telegram Views
import TelegramWrapper from "@/views/Telegram/TelegramWrapper.vue";
import TelegramHome from "@/views/Telegram/TelegramHome.vue";
import TelegramSubmit from "@/views/Telegram/TelegramSubmit.vue";
import TelegramWhitepaper from "@/views/Telegram/TelegramWhitepaper.vue";
import TelegramAbout from "@/views/Telegram/TelegramAbout.vue";

const routes = [

  // 🏠 Home
  {
    path: "/",
    name: "Home",
    component: HomePage,
    meta: {
      title: "Mudra Token - Write Stories & Earn Rewards",
      description: "Publish stories, share creativity and earn Mudra Tokens on Mudra platform."
    }
  },

  // 📄 Static Pages
  {
    path: "/whitepaper",
    name: "Whitepaper",
    component: WhitepaperPage,
    meta: {
      title: "Mudra Whitepaper",
      description: "Learn about Mudra Token project, vision and ecosystem."
    }
  },
  {
    path: "/about",
    name: "About",
    component: AboutPage,
    meta: {
      title: "About Mudra",
      description: "Discover Mudra platform for writers and readers."
    }
  },
  {
    path: "/feedback",
    name: "Feedback",
    component: FeedbackView,
    meta: {
      title: "Feedback - Mudra",
      description: "Share your feedback about Mudra platform."
    }
  },

  // 🔐 Auth
  {
    path: "/login",
    name: "Login",
    component: LoginPage,
    meta: {
      allowAnonymous: true,
      title: "Login - Mudra",
      description: "Login to your Mudra account."
    }
  },
  {
    path: "/register",
    name: "Register",
    component: RegisterPage,
    meta: {
      allowAnonymous: true,
      title: "Register - Mudra",
      description: "Create your Mudra account and get started."
    }
  },
  {
    path: "/auth/callback",
    name: "AuthCallback",
    component: AuthCallback,
    meta: { allowAnonymous: true }
  },

  {
    path: "/reset-password",
    name: "ResetPassword",
    component: () => import("@/views/ResetPassword.vue"),
    meta: {
      allowAnonymous: true,
      title: "Reset Password",
      description: "Reset your Mudra account password."
    }
  },

  // ✍️ Writing
  {
    path: "/write",
    name: "Write",
    component: SubmitStoryView,
    meta: {
      requiresAuth: true,
      title: "Write Story",
      description: "Publish your story and earn Mudra Tokens."
    }
  },

  // 📚 Stories
  {
    path: "/stories",
    name: "Stories",
    component: () => import("@/views/StoriesList.vue"),
    meta: {
      title: "Read Stories - Mudra",
      description: "Explore stories written by creators worldwide."
    }
  },

  {
    path: "/story/:id",
    name: "StoryDetail",
    component: () => import("@/views/StoryDetail.vue"),
    meta: {
      requiresAuth: true,
      title: "Read Story - Mudra",
      description: "Read full story and explore creative writing."
    }
  },

  {
    path: "/stories/:category",
    name: "StoriesCategory",
    component: () => import("@/views/StoriesList.vue"),
    props: (route) => ({ category: route.params.category }),
    meta: {
      requiresAuth: true,
      title: "Stories by Category",
      description: "Browse stories by category."
    }
  },

  {
    path: "/trending",
    name: "Trending",
    component: () => import("@/views/TrendingStories.vue"),
    meta: {
      title: "Trending Stories - Mudra",
      description: "Discover trending stories based on engagement."
    }
  },

  {
    path: "/my-stories",
    name: "MyStories",
    component: () => import("@/views/MyStories.vue"),
    meta: {
      requiresAuth: true,
      title: "My Stories",
      description: "Manage your published and draft stories."
    }
  },

  // 👤 Profile
  {
    path: "/profile",
    name: "Profile",
    component: () => import("@/views/ProfilePage.vue"),
    meta: {
      requiresAuth: true,
      title: "My Profile",
      description: "View and manage your Mudra profile."
    }
  },
  {
    path: "/profile/:id",
    name: "UserProfile",
    component: () => import("@/views/ProfilePage.vue"),
    meta: {
      requiresAuth: true,
      title: "User Profile",
      description: "Explore user profile and their stories."
    }
  },
  {
    path: "/edit-profile",
    name: "EditProfile",
    component: () => import("@/views/EditProfile.vue"),
    meta: {
      requiresAuth: true,
      title: "Edit Profile",
      description: "Update your profile details."
    }
  },

{
  path: "/requests",
  component: () => import("@/pages/RequestsPage.vue")
},

{
  path: "/followers/:id",
  component: () => import("@/pages/FollowersPage.vue"),
},
{
  path: "/following/:id",
  component: () => import("@/pages/FollowingPage.vue"),
},

  {
  path: "/chat/:id",
  name: "Chat",
  component: () => import("@/views/ChatPage.vue"),
  meta: {
    requiresAuth: true,
    title: "Chat",
    description: "Chat with connected users"
  }
},

{
  path: "/chats",
  name: "ChatList",
  component: () => import("@/views/ChatListPage.vue"),
  meta: {
    requiresAuth: true,
    title: "Messages",
    description: "View all your chats"
  }
},

  // 📜 Legal
  {
    path: "/privacy",
    name: "PrivacyPolicy",
    component: () => import("@/views/PrivacyPolicy.vue"),
    meta: {
      title: "Privacy Policy",
      description: "Read Mudra privacy policy."
    }
  },
  {
    path: "/terms",
    component: () => import("@/views/TermsAndConditions.vue"),
    meta: {
      title: "Terms & Conditions",
      description: "Read Mudra terms and conditions."
    }
  },
  

  // ⚙️ Admin
  {
    path: "/admin",
    name: "AdminPanel",
    component: () => import("@/views/AdminPanel.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/ads-admin",
    name: "AdsAdmin",
    component: () => import("@/views/AdsAdmin.vue"),
    meta: { requiresAuth: true }
  },

  // 🎯 Contest
  {
    path: "/contest/:id",
    component: ContestDetail
  },

  // 🤖 Telegram
  {
    path: "/telegram",
    component: TelegramWrapper,
    children: [
      { path: "", redirect: "/telegram/home" },
      { path: "home", component: TelegramHome },
      { path: "submit", component: TelegramSubmit },
      { path: "whitepaper", component: TelegramWhitepaper },
      { path: "about", component: TelegramAbout },
    ],
  },

  // ❌ 404
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: () => import("@/views/NotFound.vue"),
    meta: { allowAnonymous: true }
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// 🔐 Auth Guard (unchanged)
router.beforeEach(async (to, from, next) => {
  if (to.meta.allowAnonymous) return next();

  const { data } = await supabase.auth.getSession();
  const session = data?.session;

  if (to.meta.requiresAuth && !session) {
    alert(
      "🌟 Join the Mudra Community!\n\n" +
      "Login or register to unlock full stories, trending content, and start publishing your own creative work.\n\n" +
      "🎁 New users get 10 Mudra Tokens instantly."
    );
    return next({ name: "Login" });
  }

  next();
});

// 🚀 SEO: Update Title and Meta tags on route change
router.afterEach((to) => {
  // Update Title
  const defaultTitle = "Mudra | Earn Crypto by Writing Stories";
  document.title = to.meta.title || defaultTitle;

  // Update Meta Description
  const defaultDesc = "Mudra is a creative writing platform where writers earn crypto rewards. Publish stories on Mudra and earn Mudra Tokens for every approved story.";
  const description = to.meta.description || defaultDesc;

  let metaTag = document.querySelector('meta[name="description"]');
  if (!metaTag) {
    metaTag = document.createElement('meta');
    metaTag.name = "description";
    document.head.appendChild(metaTag);
  }
  metaTag.content = description;

  // 🔗 Update Canonical Link (FIX FOR GOOGLE INDEXING)
  let canonicalTag = document.querySelector('link[rel="canonical"]');
  if (!canonicalTag) {
    canonicalTag = document.createElement('link');
    canonicalTag.setAttribute('rel', 'canonical');
    document.head.appendChild(canonicalTag);
  }
  canonicalTag.setAttribute('href', window.location.origin + to.path);
});

export default router;
